import { useEditor } from '../store/useEditor'
import { sectionsOf } from '../lib/sections'
import { comparePackages, PACKAGES, PRICE_UNDECIDED } from '../lib/packages'
import { Section } from './bits'

/**
 * TWO WAYS TO LIGHT THE SAME HOUSE.
 *
 * Two, not nine. The workflow document asks for two or three meaningful
 * packages with the differences highlighted rather than a wall of similar
 * cards, and on a roofline the meaningful choice is one: big bulbs a foot
 * apart, or a tight permanent track. A third card would carry the same numbers
 * as the first.
 *
 * CHOOSING ONE CHANGES WHAT YOU DRAW WITH, and it says so on the control. The
 * package IS the product: the counts, the spacing and the look all come from
 * it, so a package that did not change the pen would be a label on nothing.
 *
 * NO PRICES. The customer comparison excludes cost and margin by design, and
 * beyond that nobody has decided what a foot of each product family sells for.
 * A comparison that invented one would be the most convincing wrong number in
 * this product, so the missing decision is on the screen instead.
 *
 * WHY THIS IS A LIST OF CHOICES AND NOT TWO CARDS.
 *
 * It was two cards side by side, one of them tinted mint with a green outline
 * to say it was selected, each holding a paragraph, a pair of boxed figures
 * and a bullet list. Boxes inside boxes inside a card, with a pale tint doing
 * the work of a control - which is the look of something assembled rather than
 * designed, and it is nothing like the source.
 *
 * The design file makes a choice exactly one way, on its payment method and
 * card pickers: a stack of bordered rows, each with a radio on the left, and
 * the chosen row filled solid green with dark type on it. That is a control. A
 * person can see which one is on from across the room, and the two options are
 * compared by reading down one column rather than across two cards.
 */
export default function Packages() {
  const doc = useEditor((s) => s.doc)
  const defaults = useEditor((s) => s.defaults)
  const setDefaults = useEditor((s) => s.setDefaults)

  const sections = sectionsOf(doc)
  if (sections.length === 0) return null

  const options = comparePackages(sections)
  const bulbsOf = (o: (typeof options)[number]) =>
    o.takeoff.lines.find((l) => l.key === 'bulbs')?.qty ?? 0

  return (
    <Section title="Two ways to do it">
      {/*
        A radiogroup, because that is what it is: one of these is on and
        choosing another turns the first off. It was a pair of divs with a
        button inside one of them, which no keyboard and no screen reader could
        read as a choice at all.
      */}
      <div className="choices" role="radiogroup" aria-label="Product package">
        {options.map((o) => {
          const chosen = defaults.product === o.pkg.product
          return (
            <button
              key={o.pkg.id}
              type="button"
              role="radio"
              aria-checked={chosen}
              className={chosen ? 'choice on' : 'choice'}
              onClick={() => setDefaults({ product: o.pkg.product })}
            >
              <span className="choice-mark" aria-hidden="true" />
              <span className="choice-body">
                <span className="choice-name">{o.pkg.name}</span>
                <span className="choice-why">{o.pkg.why}</span>
              </span>
              <span className="choice-nums">
                <b>{bulbsOf(o)}</b>
                <em>bulbs</em>
              </span>
              <span className="choice-nums">
                <b>{o.spacingInches}&thinsp;in</b>
                <em>apart</em>
              </span>
            </button>
          )
        })}
      </div>

      {/*
        THE DIFFERENCE, ONCE, UNDER THE CHOICE. It was a bullet list inside
        whichever card was not selected, so the comparison lived in the option
        you had not taken.
      */}
      {options.map((o) =>
        defaults.product !== o.pkg.product && o.differences.length > 0 ? (
          <p key={o.pkg.id} className="choice-diff">
            {o.pkg.name}: {o.differences.join(' ')}
          </p>
        ) : null,
      )}

      {/*
        THE OPEN DECISION, ON THE SCREEN RATHER THAN IN A BACKLOG.
        Somebody looking at two packages is exactly the person who will ask
        what they cost, and the honest answer is that nobody has set it yet.
      */}
      <p className="choice-note">{PRICE_UNDECIDED}</p>
      {PACKAGES.length < 2 && null}
    </Section>
  )
}
